/**
 * @module PricePointHero
 * @description
 */
import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import merge from "lodash-es/merge";
import useContentProviderProduct from "@/content-provider/hooks/useContentProviderProduct";
import { ContentProviderContext } from "@/providers/content-provider";
import DynamicButton from "@/blocks/Buttons/DynamicButton";
import defaultProducts from "@/providers/content-provider/utils/products";
import styles from "./HeroPricePoint.module.css";
import Tooltip from "@/blocks/Tooltip";

const position = "HERO PRICE POINT";

/**
 * @function Component
 * @description Hero that displays a headline and a price point
 */
const Component = ({ ...props }) => {
  const {
    headline,
    subhead,
    hash = null,
    productName = null,
    background = "lady-checking-frontier-pricing",
    useSelectAreasLegal,
    useShortLegal,
    theme = "dark",
  } = props;

  const { featuredProduct, productLoaded } = useContext(ContentProviderContext);
  const [product, setProduct] = useState(null);
  const featured = useContentProviderProduct(hash) || featuredProduct;
  const backgroundPath = `${process.env.imagePath}/sections/hero/${background}`;

  /**
   * @function useEffect
   * @description
   */
  useEffect(() => {
    if (!productLoaded) {
      return;
    }

    if (!featured) {
      setProduct(defaultProducts["fiber-gig"]);
      return;
    }

    const productData = {
      hash,
      title: featured.title,
      name: featured.name,
      price: {
        base: `$${featured.price.base}`,
        promo: `$${featured.price.promo}`,
      },
      legal: {
        hash: featured.legal.hash,
        proximity: featured.legal.proximity,
        tooltip: featured.legal.tooltip,
        short: featured.legal.short,
      },
    };

    // Merge our default data with our featured product data.
    setProduct((defaultData) => merge({ ...defaultData }, { ...productData }));
  }, [productLoaded, hash, featuredProduct, featured]);

  return (
    <section
      className={`component ${styles.component} ${theme && `u-theme-${theme} `}`}
      style={{ backgroundImage: `url(${backgroundPath}--large.jpg )` }}
      data-cypress="hero-price-point"
    >
      <div className={`l-container ${styles.component__wrapper}`}>
        <div className="g-row">
          <div className="g-col g-col--two-thirds">
            <div className={`${styles.component__contentWrapper}`}>
              <h2 className={`${styles.component__title} h1`}>{headline}</h2>
              {subhead && (
                <p className={`h4 ${styles.component__subhead}`}>{subhead}</p>
              )}

              <div className={styles.component__product}>
                {product !== null && Object.keys(product).length && (
                  <div
                    className={`product-${product.hash}`}
                    data-hash={product.hash}
                  >
                    <p className={`h5 ${styles.component__productTitle}`}>
                      {productName || `${product.name} Internet`}
                    </p>
                    <div className={styles.component__productInfo}>
                      <div className={styles.component__productPricing}>
                        <div className={styles.component__productPrice}>
                          <span
                            className="h1 h1--pricepoint"
                            data-cypress="price-point"
                          >
                            {product.price.promo}
                            <sup
                              className={styles.component__productPricingLegal}
                            >
                              <span data-legal={product.legal.hash} />
                            </sup>
                          </span>

                          <div className={styles.component__productLockup}>
                            <s className={styles.component__productPriceStrike}>
                              {product.price.base}
                            </s>
                          </div>
                        </div>
                      </div>
                      {/* <!-- .component__productPricing --> */}
                    </div>
                    {/* <!-- .component__productInfo --> */}

                    <p className={`legal ${styles.component__shortLegal}`}>
                      {useShortLegal
                        ? product.legal.short
                        : product.legal.proximity}
                      {<Tooltip disclosure={product.legal.tooltip} />}
                    </p>
                  </div>
                )}
              </div>

              <DynamicButton type="cart" position={position} />

              {useSelectAreasLegal && (
                <p className={`legal ${styles.component__selectAreasLegal} `}>
                  Frontier<sup>&reg;</sup> Fiber Internet available in
                  select&nbsp;areas.
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={styles.component__mobileBackground}
          style={{ backgroundImage: `url( ${backgroundPath}.jpg )` }}
        />
      </div>
    </section>
  );
};

Component.propTypes = {
  headline: PropTypes.node,
  subhead: PropTypes.string,
  hash: PropTypes.string,
  productName: PropTypes.node,
  background: PropTypes.string,
  useSelectAreasLegal: PropTypes.bool,
  useShortLegal: PropTypes.bool,
};

export default Component;
